import {
  PeopleOutline,
  SearchOutlined,
  CheckCircleOutlined,
  WarningAmberOutlined,
  AccessTimeOutlined,
  VisibilityOutlined,
} from '@mui/icons-material';
import { Button, CardContainer, DataTable, Tag, SearchBar, Dropdown } from 'finara-components';
import type { DataTableColumn } from 'finara-components';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CsvExportButton } from '@/components/common';
import { ErrorState } from '@/components/common/ErrorState';
import { LoadingState } from '@/components/common/LoadingState';
import { useClientEmployees } from '@/hooks/queries/useSuperAdminClients';
import type { OrganizationEmployee } from '@/types/superAdminClients';

interface ClienteFinalTabProps {
  clientId: string;
}

export const ClienteFinalTab: React.FC<ClienteFinalTabProps> = ({ clientId }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const { data: employees = [], isLoading, isError, refetch } = useClientEmployees(clientId);

  // Format currency
  const formatCurrency = (value: number) => {
    return value.toLocaleString('es-MX', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  // Filter employees
  const filteredEmployees = employees.filter(emp => {
    const matchesSearch =
      !searchTerm ||
      emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.employeeId.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = !statusFilter || emp.accountStatus === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Summary data
  const kycComplete = employees.filter(u => u.kycStatus === 'Completo').length;
  const kycIncomplete = employees.filter(u => u.kycStatus === 'Incompleto').length;
  const kycRejected = employees.filter(u => u.kycStatus === 'Rechazado').length;
  const activeAccounts = employees.filter(u => u.accountStatus === 'Activo').length;

  const getStatusVariant = (status: string): 'green' | 'yellow' | 'white' | 'red' => {
    switch (status) {
      case 'Activo':
        return 'green';
      case 'Suspendido':
        return 'red';
      default:
        return 'white';
    }
  };

  const userColumns: DataTableColumn<OrganizationEmployee>[] = [
    {
      key: 'name',
      label: 'Usuario',
      render: (_, user) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
            <PeopleOutline fontSize="small" className="text-muted" />
          </div>
          <div>
            <p className="text-sm font-medium text-brand-white">{user.name}</p>
            <p className="text-xs text-muted">{user.email || 'Sin correo'}</p>
            <p className="text-xs text-muted">{user.phone || 'Sin teléfono'}</p>
            <p className="text-xs text-muted">{user.position || 'Sin departamento'}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'balance',
      label: 'Balance',
      render: (_, user) => (
        <div>
          <p className="text-sm font-semibold text-brand-white">${formatCurrency(user.balance)}</p>
          <p className="text-xs text-muted">Límite Diario: ${formatCurrency(user.dailyLimit)}</p>
        </div>
      ),
    },
    {
      key: 'kycStatus',
      label: 'KYC',
      render: (_, user) => <Tag variant="white">{user.kycStatus}</Tag>,
    },
    {
      key: 'lastActivity',
      label: 'Última Actividad',
      render: (_, user) => (
        <div>
          <p className="text-sm text-brand-white">{formatDate(user.lastActivity)}</p>
          <p className="text-xs text-muted">Registro: {formatDate(user.registrationDate)}</p>
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Estatus',
      render: (_, user) => (
        <div className="space-y-1">
          <Tag variant={getStatusVariant(user.accountStatus)}>{user.accountStatus}</Tag>
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (_, user) => (
        <Button
          variant="white"
          size="small"
          startIcon={<VisibilityOutlined fontSize="small" />}
          onClick={() => void navigate(`/clientes/${clientId}/empleados/${user.id}`)}
        >
          Ver perfil
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:!grid-cols-2 lg:!grid-cols-4 gap-6">
        <CardContainer>
          <div className="flex items-center gap-3">
            <CheckCircleOutlined className="text-green-600" />
            <div>
              <p className="text-sm text-muted">KYC Completo</p>
              <p className="text-2xl font-bold text-brand-white">{kycComplete}</p>
            </div>
          </div>
        </CardContainer>

        <CardContainer>
          <div className="flex items-center gap-3">
            <AccessTimeOutlined className="text-yellow-600" />
            <div>
              <p className="text-sm text-muted">KYC Incompleto</p>
              <p className="text-2xl font-bold text-brand-white">{kycIncomplete}</p>
            </div>
          </div>
        </CardContainer>

        <CardContainer>
          <div className="flex items-center gap-3">
            <WarningAmberOutlined className="text-red-600" />
            <div>
              <p className="text-sm text-muted">KYC Rechazado</p>
              <p className="text-2xl font-bold text-brand-white">{kycRejected}</p>
            </div>
          </div>
        </CardContainer>

        <CardContainer>
          <div className="flex items-center gap-3">
            <PeopleOutline className="text-blue-600" />
            <div>
              <p className="text-sm text-muted">Cuentas Activas</p>
              <p className="text-2xl font-bold text-brand-white">{activeAccounts}</p>
            </div>
          </div>
        </CardContainer>
      </div>

      {/* Users List */}
      <CardContainer>
        {/* Search and Filters */}
        <div className="mb-6 space-y-4">
          <div className="flex flex-col lg:!flex-row gap-4">
            <div className="flex-1">
              <label
                htmlFor="search-users"
                className="block text-sm font-medium text-muted mb-2"
              >
                <SearchOutlined fontSize="small" className="inline mr-1" />
                Buscar por nombre o ID de empleado...
              </label>
              <SearchBar
                id="search-users"
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Buscar por nombre o ID de empleado..."
                className="w-full"
              />
            </div>

            <div className="flex gap-4">
              <div>
                <label
                  htmlFor="status-filter"
                  className="block text-sm font-medium text-muted mb-2"
                >
                  Todos los estados
                </label>
                <Dropdown
                  id="status-filter"
                  value={statusFilter}
                  onChange={value => setStatusFilter(String(value))}
                  options={[
                    { value: '', label: 'Todos los estados' },
                    { value: 'Activo', label: 'Activo' },
                    { value: 'Suspendido', label: 'Suspendido' },
                  ]}
                  placeholder="Todos los estados"
                  className="w-full lg:!w-48"
                />
              </div>
            </div>

            <div className="flex items-end">
              <CsvExportButton
                data={filteredEmployees}
                filename="clientes_finales"
                columns={emp => ({
                  Nombre: emp.name,
                  Correo: emp.email || 'N/A',
                  Teléfono: emp.phone || 'N/A',
                  Posición: emp.position || 'N/A',
                  'ID Empleado': emp.employeeId,
                  Balance: `$${formatCurrency(emp.balance)}`,
                  'Límite Diario': `$${formatCurrency(emp.dailyLimit)}`,
                  KYC: emp.kycStatus,
                  'Última Actividad': formatDate(emp.lastActivity),
                  'Fecha de Registro': formatDate(emp.registrationDate),
                  Estado: emp.accountStatus,
                })}
                variant="white"
                size="small"
                buttonText="Exportar"
                className="w-full lg:!w-auto"
              />
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div>
          <h3 className="text-base font-semibold text-brand-white mb-4">
            Clientes Finales Asociados ({filteredEmployees.length})
          </h3>
          {isLoading ? (
            <LoadingState message="Cargando empleados..." fullHeight={false} />
          ) : isError ? (
            <ErrorState
              title="Error al cargar los empleados"
              message="No se pudieron cargar los empleados. Por favor, intenta nuevamente."
              onRetry={() => void refetch()}
              useCard={false}
            />
          ) : filteredEmployees.length === 0 ? (
            <p className="text-center text-muted py-8">No se encontraron empleados</p>
          ) : (
            <div className="overflow-x-auto">
              <DataTable columns={userColumns} data={filteredEmployees} />
            </div>
          )}
        </div>
      </CardContainer>
    </div>
  );
};
